import {
  INTERACTIVE_SESSION_TOOL_NAME,
  KILL_JOB_TOOL_NAME,
  parseTerminalOutputItem,
  READ_JOB_OUTPUT_TOOL_NAME,
  RUN_BACKGROUND_TOOL_NAME,
  type TerminalExecutePayload,
  type TerminalOutputItem,
} from '@terra/shared-dto-chat-types';
import type { CommandRunLedgerEntry } from '../../../types/command-executor.types';
import type { ExternalToolResultCoordinator } from '../external-tool-batch.types';
import {
  scheduleTerminalExecution,
  type TerminalExecutionQueue,
} from '../terminal-execution-queue';
import {
  forgetInFlightTerminalExecute,
  readInFlightTerminalExecutes,
  rememberInFlightTerminalExecute,
} from './terminal-inflight-executes';
import {
  buildPortalExternalToolResult,
  buildPortalToolErrorResult,
  type PortalToolHandlerResult,
} from './types';

export const TERMINAL_IPC_WATCHDOG_MARGIN_MS = 30_000;
export const TERMINAL_IPC_DEFAULT_COMMAND_TIMEOUT_MS = 120_000;
export const TERMINAL_IPC_MAX_COMMAND_TIMEOUT_MS = 30 * 60 * 1000;
export const TERMINAL_IPC_DEFAULT_PTY_WAIT_MS = 5_000;
export const TERMINAL_IPC_MAX_PTY_WAIT_MS = 120_000;
export const TERMINAL_IPC_SHORT_CALL_MS = 60_000;

export type TerminalExecuteRunCommand = (params: {
  sessionId: string;
  batchId: string;
  toolCallId: string;
  input: Record<string, unknown>;
}) => Promise<PortalToolHandlerResult>;

export function buildCommandInput(
  payload: TerminalExecutePayload,
): Record<string, unknown> {
  return {
    command: payload.executionCommand,
    displayCommand: payload.displayCommand,
    executionCommand: payload.executionCommand,
    description: payload.description,
    cwd: payload.cwd,
    isBackground: payload.isBackground,
    timeoutMs: payload.timeoutMs,
    chatId: payload.chatId,
    toolName: payload.toolName,
    shell: payload.shell,
    idleTimeoutMs: payload.idleTimeoutMs,
    killOnTimeout: payload.killOnTimeout,
    jobId: payload.jobId,
    tailBytes: payload.tailBytes,
    ptyId: payload.ptyId,
    ptyAction: payload.ptyAction,
    ptyInput: payload.ptyInput,
    waitMs: payload.waitMs,
    tunnelRequirement: payload.tunnelRequirement,
  };
}

export function withDisplayCommand({
  handlerResult,
  displayCommand,
}: {
  handlerResult: PortalToolHandlerResult;
  displayCommand: string;
}): PortalToolHandlerResult {
  const parsed = parseTerminalOutputItem(handlerResult.result);

  if (!parsed) {
    return handlerResult;
  }

  return {
    ...handlerResult,
    result: {
      ...parsed,
      command: displayCommand,
    },
  };
}

function clamp({
  requested,
  fallback,
  max,
}: {
  requested: number | undefined;
  fallback: number;
  max: number;
}): number {
  const value =
    typeof requested === 'number' && requested > 0 ? requested : fallback;

  return Math.min(value, max);
}

export function resolveForegroundWatchdogMs(
  payload: TerminalExecutePayload,
): number {
  if (
    payload.toolName === READ_JOB_OUTPUT_TOOL_NAME ||
    payload.toolName === KILL_JOB_TOOL_NAME ||
    payload.toolName === RUN_BACKGROUND_TOOL_NAME ||
    payload.isBackground
  ) {
    return TERMINAL_IPC_SHORT_CALL_MS;
  }

  if (payload.toolName === INTERACTIVE_SESSION_TOOL_NAME) {
    return (
      clamp({
        requested: payload.waitMs,
        fallback: TERMINAL_IPC_DEFAULT_PTY_WAIT_MS,
        max: TERMINAL_IPC_MAX_PTY_WAIT_MS,
      }) + TERMINAL_IPC_WATCHDOG_MARGIN_MS
    );
  }

  return (
    clamp({
      requested: payload.timeoutMs,
      fallback: TERMINAL_IPC_DEFAULT_COMMAND_TIMEOUT_MS,
      max: TERMINAL_IPC_MAX_COMMAND_TIMEOUT_MS,
    }) + TERMINAL_IPC_WATCHDOG_MARGIN_MS
  );
}

function buildWatchdogTimeoutResult({
  payload,
  watchdogMs,
}: {
  payload: TerminalExecutePayload;
  watchdogMs: number;
}): PortalToolHandlerResult {
  const output = `Command executor did not respond within ${Math.round(watchdogMs / 1000)}s`;
  const result: TerminalOutputItem = {
    batchId: payload.batchId,
    toolCallId: payload.toolCallId,
    command: payload.displayCommand,
    exitCode: -1,
    output,
    outputLength: output.length,
    isTimeout: true,
    failureKind: 'timeout',
    timedOut: true,
  };

  return { status: 'error', result };
}

function runWithWatchdog({
  payload,
  runCommand,
  watchdogMs,
}: {
  payload: TerminalExecutePayload;
  runCommand: TerminalExecuteRunCommand;
  watchdogMs: number;
}): Promise<PortalToolHandlerResult> {
  return new Promise<PortalToolHandlerResult>((resolve, reject) => {
    let settled = false;
    const timer = setTimeout(() => {
      if (settled) {
        return;
      }

      settled = true;
      console.warn('[TerminalExecution] IPC watchdog fired', {
        toolCallId: payload.toolCallId,
        toolName: payload.toolName,
        watchdogMs,
      });
      resolve(buildWatchdogTimeoutResult({ payload, watchdogMs }));
    }, watchdogMs);

    runCommand({
      sessionId: payload.sessionId,
      batchId: payload.batchId,
      toolCallId: payload.toolCallId,
      input: buildCommandInput(payload),
    }).then(
      (handlerResult) => {
        if (settled) {
          console.warn(
            '[TerminalExecution] Ignoring late command result after watchdog',
            { toolCallId: payload.toolCallId },
          );

          return;
        }

        settled = true;
        clearTimeout(timer);
        resolve(handlerResult);
      },
      (error: unknown) => {
        if (settled) {
          return;
        }

        settled = true;
        clearTimeout(timer);
        reject(error);
      },
    );
  });
}

export async function runForegroundCommand({
  coordinator,
  payload,
  runCommand,
}: {
  coordinator: ExternalToolResultCoordinator;
  payload: TerminalExecutePayload;
  runCommand: TerminalExecuteRunCommand;
}): Promise<void> {
  coordinator.notifyHandlerStart({
    batchId: payload.batchId,
    chatId: payload.chatId,
    toolCallId: payload.toolCallId,
  });
  rememberInFlightTerminalExecute(payload);

  try {
    const handlerResult = await runWithWatchdog({
      payload,
      runCommand,
      watchdogMs: resolveForegroundWatchdogMs(payload),
    });

    await coordinator.completeRequest(
      buildPortalExternalToolResult({
        batchId: payload.batchId,
        chatId: payload.chatId,
        toolCallId: payload.toolCallId,
        toolName: payload.toolName,
        handlerResult: withDisplayCommand({
          handlerResult,
          displayCommand: payload.displayCommand,
        }),
      }),
    );
  } catch (error: unknown) {
    console.warn('[PortalToolProvider] Handler execution failed:', error);
    await coordinator.completeRequest(
      buildPortalExternalToolResult({
        batchId: payload.batchId,
        chatId: payload.chatId,
        toolCallId: payload.toolCallId,
        toolName: payload.toolName,
        handlerResult: withDisplayCommand({
          handlerResult: buildPortalToolErrorResult({
            toolName: payload.toolName,
            toolCallId: payload.toolCallId,
            batchId: payload.batchId,
            message:
              error instanceof Error
                ? error.message
                : 'Portal tool execution failed',
          }),
          displayCommand: payload.displayCommand,
        }),
      }),
    );
  } finally {
    forgetInFlightTerminalExecute(payload.toolCallId);
    coordinator.notifyHandlerEnd({
      batchId: payload.batchId,
      chatId: payload.chatId,
      toolCallId: payload.toolCallId,
    });
  }
}

const reboundToolCallIds = new Set<string>();

export const LOST_AFTER_RELOAD_MESSAGE =
  'The window reloaded before the desktop app recorded this command run, so its result is unavailable. Re-run the command if it is still needed.';

async function reportLostAfterReload({
  coordinator,
  payload,
}: {
  coordinator: ExternalToolResultCoordinator;
  payload: TerminalExecutePayload;
}): Promise<void> {
  forgetInFlightTerminalExecute(payload.toolCallId);
  coordinator.registerRequest({
    batchId: payload.batchId,
    chatId: payload.chatId,
    toolCallId: payload.toolCallId,
    toolName: payload.toolName,
  });
  await coordinator.completeRequest(
    buildPortalExternalToolResult({
      batchId: payload.batchId,
      chatId: payload.chatId,
      toolCallId: payload.toolCallId,
      toolName: payload.toolName,
      handlerResult: withDisplayCommand({
        handlerResult: buildPortalToolErrorResult({
          toolName: payload.toolName,
          toolCallId: payload.toolCallId,
          batchId: payload.batchId,
          message: LOST_AFTER_RELOAD_MESSAGE,
        }),
        displayCommand: payload.displayCommand,
      }),
    }),
  );
}

export async function rebindInFlightTerminalExecutes({
  coordinator,
  queue,
  runCommand,
  listInFlight,
}: {
  coordinator: ExternalToolResultCoordinator;
  queue: TerminalExecutionQueue;
  runCommand: TerminalExecuteRunCommand;
  listInFlight: () => Promise<CommandRunLedgerEntry[]>;
}): Promise<{ rebound: string[]; dropped: string[] }> {
  const stored = readInFlightTerminalExecutes();

  stored
    .filter((payload) => reboundToolCallIds.has(payload.toolCallId))
    .forEach((payload) => forgetInFlightTerminalExecute(payload.toolCallId));

  const persisted = stored.filter(
    (payload) => !reboundToolCallIds.has(payload.toolCallId),
  );

  if (persisted.length === 0) {
    return { rebound: [], dropped: [] };
  }

  const knownToolCallIds = new Set(
    (await listInFlight()).map((entry) => entry.toolCallId),
  );
  const rebindable = persisted.filter((payload) =>
    knownToolCallIds.has(payload.toolCallId),
  );
  const dropped = persisted.filter(
    (payload) => !knownToolCallIds.has(payload.toolCallId),
  );

  await Promise.all(
    dropped.map((payload) => reportLostAfterReload({ coordinator, payload })),
  );

  rebindable.forEach((payload) => {
    reboundToolCallIds.add(payload.toolCallId);
    coordinator.registerRequest({
      batchId: payload.batchId,
      chatId: payload.chatId,
      toolCallId: payload.toolCallId,
      toolName: payload.toolName,
    });
  });

  await Promise.all(
    rebindable.map((payload) =>
      scheduleTerminalExecution({
        sessionId: payload.sessionId,
        isBackground: false,
        execute: () =>
          runForegroundCommand({ coordinator, payload, runCommand }),
        queue,
      }),
    ),
  );

  return {
    rebound: rebindable.map((payload) => payload.toolCallId),
    dropped: dropped.map((payload) => payload.toolCallId),
  };
}
